/**
 * scanExport — Option 1 output for the Warehouse Receiving scanner.
 *
 * Builds an .xlsx whose first sheet matches the Item Master schema exactly
 * (so it can be pasted/imported into `Item_master_Data`), a second "Exceptions"
 * sheet for unresolved / pending / not-on-document rows, and a "Session" sheet
 * of metadata. GTIN, Batch/Lot, Serial and the YYMMDD dates are written as TEXT
 * (`@`) to preserve leading zeros and the 6-digit date form. Also offers CSV.
 */

import * as XLSX from 'xlsx';
import type { ScanEntry, ScanSessionMeta } from '../types/warehouse';

/** Exact Item Master header order — do not reorder. */
export const ITEM_MASTER_HEADERS = [
  'Item No.',
  'Item Description',
  'Item Group',
  'PIC',
  'GTIN',
  'Batch/Lot',
  'MFG / Production date (YYMMDD)',
  'EXP / Expiry date (YYMMDD)',
  'Serial number',
] as const;

const EXCEPTION_HEADERS = [...ITEM_MASTER_HEADERS, 'Resolution', 'Reason', 'Barcode (1D)', 'Scanned At'] as const;

/** Column indices (0-based) that must be stored as text to keep leading zeros. */
const TEXT_COLS_ITEM = [4, 5, 6, 7, 8]; // GTIN, Batch, MFG, EXP, Serial

function isResolved(e: ScanEntry): boolean {
  return e.resolution === 'matched' || e.resolution === 'mapped';
}

function itemRow(e: ScanEntry): (string | number)[] {
  return [
    e.itemNo ?? '',
    e.itemDescription ?? '',
    e.itemGroup ?? '',
    e.pic ?? '',
    e.gtin ?? '',
    e.batchLot ?? '',
    e.mfgDate ?? '',
    e.expDate ?? '',
    e.serialNumber ?? '',
  ];
}

function exceptionReason(e: ScanEntry): string {
  if (e.notOnDocument) return 'Not on PO/SO';
  if (e.resolution === 'unresolved') return 'Needs mapping';
  if (e.resolution === 'created_pending') return 'New-item request (pending)';
  if (e.overQty) return 'Over-receipt / over-delivery';
  return '';
}

/** Force a set of columns in a worksheet to text format (`@`). */
function applyTextFormat(ws: XLSX.WorkSheet, rowCount: number, cols: number[]): void {
  for (let r = 1; r <= rowCount; r++) {
    for (const c of cols) {
      const addr = XLSX.utils.encode_cell({ r, c });
      const cell = ws[addr];
      if (cell && cell.v !== '' && cell.v != null) {
        cell.t = 's';
        cell.v = String(cell.v);
        cell.z = '@';
      }
    }
  }
}

export interface BuiltWorkbook {
  workbook: XLSX.WorkBook;
  resolvedCount: number;
  exceptionCount: number;
}

/** Assemble the workbook (resolved rows + exceptions + session metadata). */
export function buildWorkbook(entries: ScanEntry[], meta: ScanSessionMeta): BuiltWorkbook {
  const resolved = entries.filter(isResolved);
  const exceptions = entries.filter((e) => !isResolved(e));

  const wb = XLSX.utils.book_new();

  // Sheet 1 — Item Master (resolved rows only).
  const itemAoa: (string | number)[][] = [ [...ITEM_MASTER_HEADERS], ...resolved.map(itemRow) ];
  const wsItems = XLSX.utils.aoa_to_sheet(itemAoa);
  applyTextFormat(wsItems, resolved.length, TEXT_COLS_ITEM);
  wsItems['!cols'] = ITEM_MASTER_HEADERS.map((h) => ({ wch: Math.max(12, h.length) }));
  XLSX.utils.book_append_sheet(wb, wsItems, 'Item_master_Data');

  // Sheet 2 — Exceptions.
  const exAoa: (string | number)[][] = [
    [...EXCEPTION_HEADERS],
    ...exceptions.map((e) => [...itemRow(e), e.resolution, exceptionReason(e), e.barcode ?? '', e.scannedAt]),
  ];
  const wsEx = XLSX.utils.aoa_to_sheet(exAoa);
  applyTextFormat(wsEx, exceptions.length, TEXT_COLS_ITEM);
  XLSX.utils.book_append_sheet(wb, wsEx, 'Exceptions');

  // Sheet 3 — Session metadata.
  const sessionAoa: (string | number)[][] = [
    ['Field', 'Value'],
    ['Mode', meta.mode],
    ['User', meta.user],
    ['Company ID', meta.companyId],
    ['Source Doc Type', meta.sourceDocType ?? ''],
    ['Source Doc No', meta.sourceDocNo ?? ''],
    ['Started At', meta.startedAt],
    ['Total Scans', entries.length],
    ['Resolved (exported)', resolved.length],
    ['Exceptions', exceptions.length],
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(sessionAoa), 'Session');

  return { workbook: wb, resolvedCount: resolved.length, exceptionCount: exceptions.length };
}

function timestamp(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}_${p(d.getHours())}${p(d.getMinutes())}`;
}

export function exportFilename(mode: string, ext: string): string {
  return `scan_export_${mode}_${timestamp()}.${ext}`;
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

/** Export to .xlsx and trigger a browser download. */
export function exportXlsx(entries: ScanEntry[], meta: ScanSessionMeta): BuiltWorkbook {
  const built = buildWorkbook(entries, meta);
  const buf = XLSX.write(built.workbook, { type: 'array', bookType: 'xlsx' });
  triggerDownload(
    new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    exportFilename(meta.mode, 'xlsx')
  );
  return built;
}

function csvCell(v: unknown): string {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Export the resolved (Item Master) rows as CSV. */
export function exportCsv(entries: ScanEntry[], meta: ScanSessionMeta): number {
  const resolved = entries.filter(isResolved);
  const lines = [
    ITEM_MASTER_HEADERS.join(','),
    ...resolved.map((e) => itemRow(e).map(csvCell).join(',')),
  ];
  triggerDownload(new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8' }), exportFilename(meta.mode, 'csv'));
  return resolved.length;
}
